import { useEffect, useState } from "react";
import { Save } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { AdminTitle } from "@/admin/AdminLayout";
import { Field } from "@/admin/FormDialog";
import { ImageField } from "@/admin/ImageField";
import { ReaderSwitches, LegalEditor } from "@/admin/AdminReaders";

export default function AdminSettings() {
  const { settings, refresh } = useSite();
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (settings.site_title !== undefined && !f) setF(settings); }, [settings, f]);
  if (!f) return null;
  const set = (p) => setF((x) => ({ ...x, ...p }));

  const save = async () => {
    setBusy(true);
    try { await api.put("/settings", f); await refresh(); toast.success("Settings saved"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-settings">
      <AdminTitle eyebrow="The imprint" title="Settings">
        <button onClick={save} disabled={busy} data-testid="settings-save-button" className="btn-illum theme-flame disabled:opacity-60"><Save className="w-4 h-4" /> {busy ? "Saving…" : "Save"}</button>
      </AdminTitle>
      <div className="grid xl:grid-cols-12 gap-6">
        <section className="xl:col-span-7 vellum p-6 grid gap-4">
          <Field label="Site title"><input data-testid="settings-title-input" value={f.site_title} onChange={(e) => set({ site_title: e.target.value })} className="admin-input" /></Field>
          <Field label="Tagline"><input data-testid="settings-tagline-input" value={f.tagline} onChange={(e) => set({ tagline: e.target.value })} className="admin-input" /></Field>
          <Field label="Home page introduction"><textarea rows={4} data-testid="settings-intro-input" value={f.intro} onChange={(e) => set({ intro: e.target.value })} className="admin-input" /></Field>
          <Field label="Footer text"><input value={f.footer_text} onChange={(e) => set({ footer_text: e.target.value })} className="admin-input" /></Field>
          <Field label="Map title"><input value={f.map_caption} onChange={(e) => set({ map_caption: e.target.value })} className="admin-input" /></Field>
          <p className="text-sm italic text-[#5e4a35] border-t border-[#c9a227]/40 pt-4">Looking for the merchandise page? It lives under <b>Royal Vault</b> in the left menu.</p>
        </section>
        <section className="xl:col-span-5 vellum p-6 grid gap-6">
          <div className="w-48"><ImageField label="Imprint logo (square)" value={f.logo_url} onChange={(v) => set({ logo_url: v })} aspect="aspect-square" testid="settings-logo-field" hint="Shown in the header, home page & footer." /></div>
          <ImageField label="Home page frontispiece" value={f.hero_image} onChange={(v) => set({ hero_image: v })} aspectKey={f.home_hero_aspect} onAspect={(v) => set({ home_hero_aspect: v })} aspectFallback="classic" testid="settings-hero-field" />
          <ImageField label="Map of the realms" value={f.map_image} onChange={(v) => set({ map_image: v })} aspectKey={f.map_aspect} onAspect={(v) => set({ map_aspect: v })} aspectFallback="portrait" testid="settings-map-field" hint="Pick a tall shape (Portrait / Tall / Scroll) for a map that is longer than it is wide." />
        </section>
      </div>
      <div className="mt-6"><ReaderSwitches f={f} set={set} /></div>
      <LegalEditor />
    </div>
  );
}
